import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type {
  EarningProfile,
  EarningLineItem,
  EarningProfileData,
  EarningLineItemData,
  EarningType,
} from "../../../types";
import { EARNING_TYPES } from "../../../types";

/**
 * Earning Profiles Store
 * Manages earning profile templates for payroll
 */
class EarningProfilesStore {
  rootStore: RootStore;

  // UI State
  selectedProfileId: string | null = null;
  isProfileDialogOpen = false;
  isLineItemDialogOpen = false;
  editingProfile: Partial<EarningProfile> | null = null;
  editingLineItem: Partial<EarningLineItem> | null = null;
  searchQuery = "";
  filterActive = true;
  isLoading = false;
  isSaving = false;

  // DataGrid configuration
  columns = [
    { key: "name", label: "Profile Name", width: 200 },
    { key: "description", label: "Description", width: 250 },
    { key: "companyId", label: "Company", width: 120 },
    { key: "itemCount", label: "Items", width: 80, align: "center" as const },
    { key: "isDefault", label: "Default", width: 80, align: "center" as const },
    { key: "isActive", label: "Active", width: 80, align: "center" as const },
  ];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ==================== Computed Properties ====================

  get profiles(): EarningProfile[] {
    const store = this.rootStore.serviceStores?.["earning-profiles"];
    return (store?.entities as EarningProfile[]) || [];
  }

  get lineItems(): EarningLineItem[] {
    const store = this.rootStore.serviceStores?.["earning-line-items"];
    return (store?.entities as EarningLineItem[]) || [];
  }

  get filteredProfiles(): EarningProfile[] {
    let result = this.profiles;

    if (this.filterActive) {
      result = result.filter((p) => p.isActive);
    }

    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          p.description?.toLowerCase().includes(query)
      );
    }

    return result;
  }

  get filteredCount(): number {
    return this.filteredProfiles.length;
  }

  get selectedProfile(): EarningProfile | undefined {
    if (!this.selectedProfileId) return undefined;
    return this.profiles.find((p) => p.earningProfileId === this.selectedProfileId);
  }

  get selectedProfileLineItems(): EarningLineItem[] {
    if (!this.selectedProfileId) return [];
    return this.lineItems
      .filter((li) => li.earningProfileId === this.selectedProfileId)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  get gridData() {
    return this.filteredProfiles.map((profile) => ({
      id: profile.earningProfileId,
      name: profile.name,
      description: profile.description || "-",
      companyId: profile.companyId,
      itemCount: this.lineItems.filter(
        (li) => li.earningProfileId === profile.earningProfileId
      ).length,
      isDefault: profile.isDefault ? "Yes" : "No",
      isActive: profile.isActive ? "Active" : "Inactive",
    }));
  }

  get earningTypeOptions() {
    return EARNING_TYPES.map((et) => ({
      value: et.type,
      label: et.label,
      description: et.description,
    }));
  }

  // ==================== Actions ====================

  setSearchQuery = (query: string) => {
    this.searchQuery = query;
  };

  setFilterActive = (active: boolean) => {
    this.filterActive = active;
  };

  setSelectedProfileId = (id: string | null) => {
    this.selectedProfileId = id;
  };

  // Profile Dialog Actions
  openCreateProfileDialog = () => {
    this.editingProfile = {
      name: "",
      description: "",
      companyId: "",
      isActive: true,
      isDefault: false,
    };
    this.isProfileDialogOpen = true;
  };

  openEditProfileDialog = (profile: EarningProfile) => {
    this.editingProfile = { ...profile };
    this.isProfileDialogOpen = true;
  };

  closeProfileDialog = () => {
    this.isProfileDialogOpen = false;
    this.editingProfile = null;
  };

  updateEditingProfile = (field: keyof EarningProfile, value: unknown) => {
    if (this.editingProfile) {
      (this.editingProfile as Record<string, unknown>)[field] = value;
    }
  };

  // Line Item Dialog Actions
  openCreateLineItemDialog = () => {
    if (!this.selectedProfileId) return;
    this.editingLineItem = {
      earningProfileId: this.selectedProfileId,
      earningCode: "",
      earningType: "hourly" as EarningType,
      description: "",
      glAccountId: "",
      showEveryTime: true,
      isActive: true,
      sortOrder: this.selectedProfileLineItems.length + 1,
    };
    this.isLineItemDialogOpen = true;
  };

  openEditLineItemDialog = (lineItem: EarningLineItem) => {
    this.editingLineItem = { ...lineItem };
    this.isLineItemDialogOpen = true;
  };

  closeLineItemDialog = () => {
    this.isLineItemDialogOpen = false;
    this.editingLineItem = null;
  };

  updateEditingLineItem = (field: keyof EarningLineItem, value: unknown) => {
    if (this.editingLineItem) {
      (this.editingLineItem as Record<string, unknown>)[field] = value;
    }
  };

  // ==================== API Actions ====================

  loadProfiles = async () => {
    this.isLoading = true;
    try {
      const profileStore = this.rootStore.serviceStores?.["earning-profiles"];
      const lineItemStore = this.rootStore.serviceStores?.["earning-line-items"];

      await Promise.all([
        profileStore?.find({ query: { $limit: 1000 } }),
        lineItemStore?.find({ query: { $limit: 5000 } }),
      ]);
    } catch (error) {
      console.error("Failed to load earning profiles:", error);
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  saveProfile = async (): Promise<boolean> => {
    if (!this.editingProfile) return false;

    this.isSaving = true;
    try {
      const profileStore = this.rootStore.serviceStores?.["earning-profiles"];

      const data: EarningProfileData = {
        name: this.editingProfile.name || "",
        description: this.editingProfile.description,
        companyId: this.editingProfile.companyId || "",
        plantId: this.editingProfile.plantId,
        isActive: this.editingProfile.isActive ?? true,
        isDefault: this.editingProfile.isDefault ?? false,
      };

      if (this.editingProfile.earningProfileId) {
        await profileStore?.patch(this.editingProfile.earningProfileId, data);
      } else {
        await profileStore?.create(data);
      }

      this.closeProfileDialog();
      await this.loadProfiles();
      return true;
    } catch (error) {
      console.error("Failed to save earning profile:", error);
      return false;
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  };

  deleteProfile = async (profileId: string): Promise<boolean> => {
    try {
      const profileStore = this.rootStore.serviceStores?.["earning-profiles"];
      await profileStore?.remove(profileId);
      await this.loadProfiles();
      if (this.selectedProfileId === profileId) {
        this.selectedProfileId = null;
      }
      return true;
    } catch (error) {
      console.error("Failed to delete earning profile:", error);
      return false;
    }
  };

  saveLineItem = async (): Promise<boolean> => {
    if (!this.editingLineItem) return false;

    this.isSaving = true;
    try {
      const lineItemStore = this.rootStore.serviceStores?.["earning-line-items"];

      const data: EarningLineItemData = {
        earningProfileId: this.editingLineItem.earningProfileId || "",
        earningCode: this.editingLineItem.earningCode || "",
        earningType: this.editingLineItem.earningType || "hourly",
        description: this.editingLineItem.description || "",
        glAccountId: this.editingLineItem.glAccountId || "",
        showEveryTime: this.editingLineItem.showEveryTime ?? true,
        isActive: this.editingLineItem.isActive ?? true,
        sortOrder: this.editingLineItem.sortOrder || 0,
        multiplier: this.editingLineItem.multiplier,
      };

      if (this.editingLineItem.earningLineItemId) {
        await lineItemStore?.patch(this.editingLineItem.earningLineItemId, data);
      } else {
        await lineItemStore?.create(data);
      }

      this.closeLineItemDialog();
      await this.loadProfiles();
      return true;
    } catch (error) {
      console.error("Failed to save earning line item:", error);
      return false;
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  };

  deleteLineItem = async (lineItemId: string): Promise<boolean> => {
    try {
      const lineItemStore = this.rootStore.serviceStores?.["earning-line-items"];
      await lineItemStore?.remove(lineItemId);
      await this.loadProfiles();
      return true;
    } catch (error) {
      console.error("Failed to delete earning line item:", error);
      return false;
    }
  };

  reorderLineItems = async (fromIndex: number, toIndex: number): Promise<boolean> => {
    if (!this.selectedProfileId) return false;

    const items = [...this.selectedProfileLineItems];
    const [movedItem] = items.splice(fromIndex, 1);
    items.splice(toIndex, 0, movedItem);

    // Update sort orders
    const lineItemStore = this.rootStore.serviceStores?.["earning-line-items"];
    try {
      await Promise.all(
        items.map((item, index) =>
          lineItemStore?.patch(item.earningLineItemId, { sortOrder: index + 1 })
        )
      );
      await this.loadProfiles();
      return true;
    } catch (error) {
      console.error("Failed to reorder line items:", error);
      return false;
    }
  };

  // ==================== Utility Methods ====================

  getEarningTypeLabel = (type: EarningType): string => {
    const config = EARNING_TYPES.find((et) => et.type === type);
    return config?.label || type;
  };

  duplicateProfile = async (profileId: string): Promise<boolean> => {
    const profile = this.profiles.find((p) => p.earningProfileId === profileId);
    if (!profile) return false;

    try {
      const profileStore = this.rootStore.serviceStores?.["earning-profiles"];
      const lineItemStore = this.rootStore.serviceStores?.["earning-line-items"];

      // Create new profile
      const newProfile = await profileStore?.create({
        name: `${profile.name} (Copy)`,
        description: profile.description,
        companyId: profile.companyId,
        plantId: profile.plantId,
        isActive: true,
        isDefault: false,
      });

      if (newProfile?.earningProfileId) {
        // Copy line items
        const profileLineItems = this.lineItems.filter(
          (li) => li.earningProfileId === profileId
        );

        await Promise.all(
          profileLineItems.map((li) =>
            lineItemStore?.create({
              earningProfileId: newProfile.earningProfileId,
              earningCode: li.earningCode,
              earningType: li.earningType,
              description: li.description,
              glAccountId: li.glAccountId,
              showEveryTime: li.showEveryTime,
              isActive: li.isActive,
              sortOrder: li.sortOrder,
              multiplier: li.multiplier,
            })
          )
        );
      }

      await this.loadProfiles();
      return true;
    } catch (error) {
      console.error("Failed to duplicate profile:", error);
      return false;
    }
  };
}

export default EarningProfilesStore;
